import type { CreateOTCForwardProduct } from '@src/types/OTC';
import { isForwardProductFields } from '@src/types/OTC/otcGuards';
import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import type { OTCForwardProductTemplate } from '@talos/kyoko';
import { logger, runValidationV1WithResult } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { BaseProductDefinition, type CreateProductParams } from './BaseProductDefinition';
import { forwardProductBuilderSchema, type ValidatedForwardRowData } from './ForwardProductBuilderSliceStrategy';

export class ForwardProductDefinition extends BaseProductDefinition {
  private otcForwardProductTemplatesByDisplaySymbol: Map<string, OTCForwardProductTemplate> = new Map();

  updateReferenceData(otcForwardProductTemplatesByDisplaySymbol: Map<string, OTCForwardProductTemplate>) {
    this.otcForwardProductTemplatesByDisplaySymbol = otcForwardProductTemplatesByDisplaySymbol;
  }

  buildPostBody(row: ValidatedForwardRowData): CreateOTCForwardProduct | undefined {
    const validationRun = runValidationV1WithResult(forwardProductBuilderSchema, row);
    if (validationRun.result === 'failure') {
      return;
    }

    const validatedData = validationRun.validatedData;

    const deliveryAsset =
      validatedData.otcFields.payoffType === 'Inverse'
        ? validatedData.baseAsset
        : validatedData.otcFields.deliveryAsset || validatedData.otcFields.quoteAsset;

    const baseAsset = validatedData.baseAsset;
    // Index is required and should be provided from formData
    const index = validatedData.otcFields.index;
    const result: CreateOTCForwardProduct = {
      ProductType: 'OTC',
      SubProductType: 'OTCForward',
      RequestType: 'OTC',
      ProductDetails: {
        PayoffType: validatedData.otcFields.payoffType,
        SettlementType: validatedData.otcFields.settlementType,
        Index: index,
        BaseAsset: baseAsset,
        Tenor: validatedData.otcFields.tenor,
        QuoteAsset: validatedData.otcFields.quoteAsset,
        DeliveryAsset: deliveryAsset,
        UnitNotionalAmount: validatedData.otcFields.unitNotionalAmount,
        ...(validatedData.uniqueSuffix && { UniqueSuffix: validatedData.uniqueSuffix }),
      },
    };
    return result;
  }

  getProductDetailsForComparison(row: DealProductBuilderRow): CreateOTCForwardProduct | null {
    if (!row.otcFields || !row.baseAsset || !isForwardProductFields(row.otcFields)) {
      return null;
    }

    const validation = runValidationV1WithResult(forwardProductBuilderSchema, row);
    if (validation.result === 'failure') {
      throw new Error('Product builder validation failed');
    }

    return this.buildPostBody(validation.validatedData) || null;
  }

  checkForUniqueness(
    rowData: DealProductBuilderRow & { product: 'forward' }
  ): 'notReady' | 'uncreated' | 'conflict' | 'alreadyCreated' {
    const validation = runValidationV1WithResult(forwardProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      return 'notReady';
    }

    const productDetails = this.getProductDetailsForComparison(rowData);
    if (!productDetails) {
      return 'notReady';
    }

    const postBody = this.buildPostBody(validation.validatedData);
    if (!postBody) {
      return 'notReady';
    }
    const symbol =
      validation.validatedData.productTemplate +
      (postBody.ProductDetails.UniqueSuffix ? `-${postBody.ProductDetails.UniqueSuffix}` : '');

    const existingSecurity = this.otcForwardProductTemplatesByDisplaySymbol.get(symbol);
    if (!existingSecurity) {
      return 'uncreated';
    }

    const existingProductDetails = existingSecurity?.ProductDetails;
    if (!isEqual(postBody.ProductDetails, existingProductDetails)) {
      return 'conflict';
    }

    return 'alreadyCreated';
  }

  async createProduct({
    rowData,
    createCustomInstrument,
    onSuccess,
    onError,
  }: CreateProductParams & {
    rowData: DealProductBuilderRow & { product: 'forward' };
  }): Promise<{ symbol: string } | undefined> {
    const validation = runValidationV1WithResult(forwardProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      logger.warn(`Failed to validate OTC Forward: ${JSON.stringify(validation.errors)}`);
      onError('Validation failed on OTC Forward');
      return;
    }

    try {
      this.checkForUniqueness(rowData);
      const forward = this.buildPostBody(validation.validatedData);
      if (!forward) {
        onError('Failed to build forward product body');
        return;
      }

      const rows = await createCustomInstrument(forward);
      onSuccess('Forward product created successfully');
      logger.info('Forward product created successfully', { data: rows });

      return { symbol: rows[0]?.Symbol ?? '' };
    } catch (error) {
      if (error instanceof Error && error.name === 'ValidationError') {
        onError(`Validation failed: ${error.message}`);
      } else {
        const serverMessage = this.getServerErrorMessage(error);
        onError(serverMessage ? `Error creating forward product: ${serverMessage}` : 'Error creating forward product');
      }
      logger.error(new Error('Error creating forward product'), {
        extra: {
          input: { rowData },
          message: error instanceof Error ? error.message : 'Unknown error',
          error,
        },
      });
      return;
    }
  }
}
